import { useTranslation } from 'react-i18next'
import { AlertTriangle, Download, RefreshCw } from 'lucide-react'

interface VersionConflictDialogProps {
  serverVersion: number
  sentBy?: string
  createdAt?: string
  onDownload: () => void
  onDismiss: () => void
  isDownloading: boolean
}

export function VersionConflictDialog({
  serverVersion,
  sentBy,
  createdAt,
  onDownload,
  onDismiss,
  isDownloading,
}: VersionConflictDialogProps) {
  const { t } = useTranslation()

  const formattedDate = createdAt
    ? new Date(createdAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-4">
      <div className="w-12 h-12 rounded-full bg-warning-50 dark:bg-warning-900/30 flex items-center justify-center">
        <AlertTriangle size={24} className="text-warning-500" />
      </div>

      <div>
        <h3 className="text-sm font-semibold text-neutral-800 dark:text-neutral-100 mb-1">
          {t('negotiate.conflict.title')}
        </h3>
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          {t('negotiate.conflict.message', { version: serverVersion })}
        </p>

        {/* Uploader info */}
        {(sentBy || formattedDate) && (
          <p className="text-[11px] text-neutral-400 dark:text-neutral-500 mt-1.5">
            {sentBy && formattedDate
              ? t('negotiate.conflict.uploadedByAt', 'Uploaded by {{name}} on {{date}}', { name: sentBy, date: formattedDate })
              : sentBy
                ? t('negotiate.conflict.uploadedBy', 'Uploaded by {{name}}', { name: sentBy })
                : t('negotiate.conflict.uploadedAt', 'Uploaded {{date}}', { date: formattedDate })}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2 w-full max-w-[220px]">
        <button
          onClick={onDownload}
          disabled={isDownloading}
          className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 rounded-lg transition-colors"
        >
          <Download size={12} />
          {isDownloading ? t('negotiate.conflict.downloading') : t('negotiate.conflict.downloadAndRetry', 'Download & continue editing')}
        </button>
        <button
          onClick={onDismiss}
          className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 transition-colors"
        >
          <RefreshCw size={10} />
          {t('negotiate.conflict.dismiss')}
        </button>
      </div>
    </div>
  )
}
